import React, { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel,
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem,
  useSidebar 
} from '@/components/ui/sidebar';
import { BookOpen, ChevronDown, ChevronRight, PlayCircle, CheckCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { useAssignedCoursesOnly } from '@/hooks/useAssignedCoursesOnly';
import { Skeleton } from '@/components/ui/skeleton';
import { generateUnifiedCourseRoute, getCategoryListingRoute } from '@/utils/unifiedRouteUtils';
import { useEnhancedNavigation } from '@/hooks/useEnhancedNavigation';
import { LoadingOverlay } from '@/components/navigation/LoadingOverlay';
import { useUserCourseAssignments } from '@/hooks/useUserCourseAssignments';

export const UnifiedCourseNavigationSection = () => {
  const { state } = useSidebar();
  const location = useLocation();
  // Use assignment-aware hook to filter courses based on user role and assignments
  const { courses, loading, error, source, assignmentCount } = useAssignedCoursesOnly();
  const { assignments } = useUserCourseAssignments();
  const { isNavigating } = useEnhancedNavigation();
  
  // Create a map of course status from assignments for quick lookup
  const courseStatusMap = useMemo(() => {
    const statusMap = new Map<string, string>();
    assignments.forEach(a => {
      statusMap.set(a.course_id, a.status);
    });
    return statusMap;
  }, [assignments]);
  
  console.log('🎨 [UnifiedCourseNavigationSection] Render:', {
    coursesCount: courses.length,
    assignmentCount,
    loading,
    error,
    completedCourses: Array.from(courseStatusMap.entries()).filter(([_, status]) => status === 'completed').length
  });
  
  // Simple route checking function
  
  // Transform courses into categories for display
  const transformedCategories = useMemo(() => {
    if (!courses.length) return [];
    
    const categoryMap = new Map();
    
    courses.forEach(course => {
      const categoryId = course.category;
      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          id: categoryId,
          title: categoryId.charAt(0).toUpperCase() + categoryId.slice(1).replace('-', ' '),
          route: getCategoryListingRoute(categoryId),
          iconComponent: BookOpen,
          courseCount: 0,
          completedCount: 0,
          courses: []
        });
      }
      
      const category = categoryMap.get(categoryId);
      const courseStatus = courseStatusMap.get(course.id);
      const isCompleted = courseStatus === 'completed';
      
      category.courseCount++;
      if (isCompleted) {
        category.completedCount++;
      }
      category.courses.push({
        id: course.id,
        title: course.title,
        level: course.level,
        duration_hours: course.duration_hours || 2,
        status: courseStatus || 'active'
      });
    });
    
    return Array.from(categoryMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [courses, courseStatusMap]);

  const isCourseRouteActive = (pathname: string) => pathname.includes('/courses');
  const unifiedTotalCourses = courses.length;
  const unifiedTotalCategories = transformedCategories.length;

  const [isExpanded, setIsExpanded] = useState(() => isCourseRouteActive(location.pathname));
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const isActive = (path: string) => location.pathname === path;
  const isActivePath = (path: string) => location.pathname.startsWith(path);

  const getNavClass = (path: string) => 
    isActive(path) || isActivePath(path) 
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 font-medium" 
      : "hover:bg-muted/50";

  const getParentNavClass = () =>
    isCourseRouteActive(location.pathname) 
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 font-medium" 
      : "hover:bg-muted/50";

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Collapsed state
  if (state === "collapsed") {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <NavLink to="/courses" className={getParentNavClass()}>
                  <BookOpen className="h-4 w-4" />
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  // Loading state
  if (loading) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <Skeleton className="h-4 w-24" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  // Error state
  if (error) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton className="text-red-500">
                <BookOpen className="h-4 w-4" />
                <span>Failed to load courses</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  // Empty state - no assigned courses or courses being set up
  if (!loading && courses.length === 0 && assignmentCount > 0) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton className="text-yellow-600 dark:text-yellow-400">
                <BookOpen className="h-4 w-4" />
                <div className="flex flex-col gap-1">
                  <span className="text-sm">Setting up courses...</span>
                  <span className="text-xs text-muted-foreground">
                    {assignmentCount} course{assignmentCount !== 1 ? 's' : ''} assigned
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <>
      <LoadingOverlay isVisible={isNavigating} message="Loading course..." />
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton className={getParentNavClass()}>
                    <BookOpen className="h-4 w-4" />
                    <span>Course Categories</span>
                    <div className="ml-auto flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px] px-1">
                        {unifiedTotalCategories}+
                      </Badge>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <SidebarMenu className="ml-4 mt-2">
                    {/* All Courses Link */}
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink to="/courses" className={getNavClass('/courses')}>
                          <BookOpen className="h-3.5 w-3.5" />
                          <span className="text-sm">All Courses</span>
                          <Badge variant="outline" className="text-xs ml-auto">
                            {unifiedTotalCourses}+
                          </Badge>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {/* Category Navigation */}
                    {transformedCategories.map((category) => {
                      const isCategoryExpanded = expandedCategories.has(category.id);
                      const IconComponent = category.iconComponent || BookOpen;
                      
                      return (
                        <Collapsible key={category.id} open={isCategoryExpanded} onOpenChange={() => toggleCategoryExpansion(category.id)}>
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton className="group w-full justify-between">
                                <div className="flex items-center gap-2">
                                  <IconComponent className="h-3.5 w-3.5" />
                                  <span className="text-sm">{category.title}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {category.courseCount}
                                  </Badge>
                                </div>
                                <ChevronRight className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            
                            <CollapsibleContent>
                              <SidebarMenu className="ml-4 mt-1">
                                {/* Category Overview Link */}
                                <SidebarMenuItem>
                                  <SidebarMenuButton asChild className="py-1.5">
                                    <NavLink to={category.route} className={getNavClass(category.route)}>
                                      <BookOpen className="h-3 w-3" />
                                      <span className="text-xs font-medium">
                                        View All {category.title}
                                      </span>
                                    </NavLink>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Individual Courses */}
                                {category.courses.map((course) => {
                                  const isCompleted = course.status === 'completed';
                                  return (
                                    <SidebarMenuItem key={course.id}>
                                      <SidebarMenuButton asChild className="py-1.5">
                                       <NavLink 
                                          to={generateUnifiedCourseRoute({ 
                                            courseId: course.id, 
                                            categoryId: category.id 
                                          })} 
                                          className={getNavClass(generateUnifiedCourseRoute({ 
                                            courseId: course.id, 
                                            categoryId: category.id 
                                          }))}
                                        >
                                          {isCompleted ? (
                                            <CheckCircle className="h-3 w-3 text-green-500" />
                                          ) : (
                                            <PlayCircle className="h-3 w-3" />
                                          )}
                                          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                            <div className="flex items-center gap-1">
                                              <span className="text-xs font-medium truncate">
                                                {course.title}
                                              </span>
                                              {isCompleted && (
                                                <Badge variant="success" className="text-[8px] px-1 py-0 h-4">
                                                  ✓
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                              <span>{course.level}</span>
                                              <span>•</span>
                                              <span>{course.duration_hours > 0 ? `${course.duration_hours}h` : 'TBD'}</span>
                                            </div>
                                          </div>
                                        </NavLink>
                                      </SidebarMenuButton>
                                    </SidebarMenuItem>
                                  );
                                })}
                              </SidebarMenu>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      );
                    })}
                  </SidebarMenu>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
};